import { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, ArrowRight, ChevronRight, Expand, Info } from 'lucide-react'
import { ROUTES, contactPath, productPath } from '@/lib/constants'
import { PRODUCTS, getProductBySlug, type Product } from '@/lib/products'
import { cn } from '@/lib/utils'
import Reveal from '@/components/shared/Reveal'
import SectionHeader from '@/components/shared/SectionHeader'
import ProductVisual from '@/components/shared/ProductVisual'
import CtaBand from '@/components/shared/CtaBand'
import Lightbox from '@/components/shared/Lightbox'
import { BrowserFrame, PhoneFrame } from '@/components/shared/DeviceFrame'
import { PlatformChips, ProductIconTile, ProductPrimaryAction, StatusBadge } from '@/components/shared/ProductBits'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import NotFound from './NotFound'

type TextItem = { title: string; description: string }
type FaqItem = { q: string; a: string }

/** B2B products get "Request a demo"; self-serve products point to "How it works". */
const DEMO_PRODUCTS = new Set(['bms', 'bell', 'mosque'])

export default function ProductPage() {
  const { slug = '' } = useParams()
  const product = getProductBySlug(slug)
  if (!product) return <NotFound />
  // keyed so local state (lightbox) resets between products
  return <ProductPageInner key={product.id} product={product} />
}

function ProductPageInner({ product }: { product: Product }) {
  const { t, i18n } = useTranslation(['products', 'common'])
  const id = product.id
  const [lightbox, setLightbox] = useState<string | null>(null)

  const features = t(`${id}.features`, { returnObjects: true }) as TextItem[]
  const steps = t(`${id}.steps`, { returnObjects: true }) as TextItem[]
  const faq = t(`${id}.faq`, { returnObjects: true }) as FaqItem[]
  const highlights = t(`${id}.highlights`, { returnObjects: true }) as string[]
  const hasScreens = product.screens.length > 0
  const hasDisclaimer = i18n.exists(`products:${id}.disclaimer`)
  const hasStatusNote = i18n.exists(`products:${id}.statusNote`)

  const sections = [
    { id: 'overview', label: t('page.overview') },
    { id: 'features', label: t('page.features') },
    { id: 'how', label: t('page.howItWorks') },
    ...(hasScreens ? [{ id: 'screens', label: t('page.screens') }] : []),
    { id: 'faq', label: t('page.faq') },
  ]

  return (
    <>
      <Helmet>
        <title>{`${t(`${id}.name`)} · ${t(`${id}.fullName`)} | Tdrem`}</title>
        <meta name="description" content={t(`${id}.tagline`)} />
        <link rel="canonical" href={`https://www.tdrem.com${productPath(product.slug)}`} />
      </Helmet>

      <Lightbox src={lightbox} onClose={() => setLightbox(null)} />

      {/* ------------------------------------------------------------ Hero */}
      <section className="relative -mt-16 overflow-hidden pt-16">
        <div aria-hidden className="bg-grid absolute inset-0 -z-10" />
        <div aria-hidden className={cn('absolute -top-32 end-0 -z-10 h-[420px] w-[620px] rounded-full blur-3xl', product.accent.glow)} />

        <div className="container py-8 sm:py-10 md:py-16">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="mb-6 flex items-center gap-1.5 text-sm text-muted-foreground sm:mb-8">
            <Link to={ROUTES.PRODUCTS} className="hover:text-foreground">
              {t('common:nav.products')}
            </Link>
            <ChevronRight className="h-3.5 w-3.5 shrink-0 flip-rtl" />
            <span className="truncate text-foreground">{t(`${id}.name`)}</span>
          </nav>

          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
            <div className="space-y-5 sm:space-y-6">
              <div className="flex items-start gap-3 sm:items-center">
                <ProductIconTile product={product} size="lg" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className={cn('text-sm font-semibold', product.accent.text)}>{t(`${id}.category`)}</p>
                    <StatusBadge status={product.status} className="shrink-0" />
                  </div>
                  <p className="mt-0.5 text-sm text-muted-foreground">{t(`${id}.fullName`)}</p>
                </div>
              </div>

              <h1 className="text-3xl font-extrabold leading-[1.1] sm:text-5xl lg:text-[3.4rem]">{t(`${id}.name`)}</h1>
              <p className="max-w-xl text-base leading-relaxed text-muted-foreground sm:text-xl">{t(`${id}.tagline`)}</p>

              <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:flex-wrap sm:items-center">
                <ProductPrimaryAction product={product} size="lg" />
                {DEMO_PRODUCTS.has(id) ? (
                  <Link
                    to={contactPath({ product: id, topic: 'demo' })}
                    className="inline-flex h-12 items-center justify-center gap-2 rounded-xl border bg-card px-6 font-medium transition-colors hover:bg-accent"
                  >
                    {t('common:actions.requestDemo')}
                  </Link>
                ) : (
                  <a
                    href="#how"
                    className="inline-flex h-12 items-center justify-center gap-2 rounded-xl border bg-card px-6 font-medium transition-colors hover:bg-accent"
                  >
                    {t('page.seeHow')}
                  </a>
                )}
              </div>

              {hasStatusNote && (
                <p className="flex max-w-xl items-start gap-2 rounded-xl border bg-card/70 p-3 text-sm text-muted-foreground">
                  <Info className={cn('mt-0.5 h-4 w-4 shrink-0', product.accent.text)} />
                  {t(`${id}.statusNote`)}
                </p>
              )}

              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-muted-foreground">
                <span>{t('page.platforms')}:</span>
                <PlatformChips platforms={product.platforms} />
                {product.status !== 'comingSoon' && (
                  <span className="text-xs" dir="ltr">
                    · {product.appHost}
                  </span>
                )}
              </div>
            </div>

            <div className="relative">
              <ProductVisual product={product} />
              {!hasScreens && (
                <p className="mt-4 text-center text-xs text-muted-foreground">{t('page.previewNote')}</p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ----------------------------------------------------- Sticky sub-nav */}
      <div className="sticky top-16 z-30 border-y bg-background/85 backdrop-blur-lg">
        <div className="container flex items-center gap-4">
          <nav className="no-scrollbar -mx-5 flex flex-1 gap-0.5 overflow-x-auto px-5 py-2 sm:mx-0 sm:gap-1 sm:px-0" aria-label={t(`${id}.name`)}>
            {sections.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className="shrink-0 rounded-lg px-2 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground sm:px-3 sm:text-sm"
              >
                {s.label}
              </a>
            ))}
          </nav>
          <ProductPrimaryAction product={product} size="sm" className="hidden shrink-0 sm:inline-flex" />
        </div>
      </div>

      {/* --------------------------------------------------------- Overview */}
      <section id="overview" className="container py-16 md:py-24">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1.1fr_1fr] lg:gap-16">
          <Reveal className="space-y-5">
            <span className="eyebrow">{t('page.overview')}</span>
            <h2 className="text-2xl font-bold leading-tight sm:text-3xl md:text-4xl">{t(`${id}.tagline`)}</h2>
            <p className="text-base leading-relaxed text-muted-foreground sm:text-lg">{t(`${id}.description`)}</p>
            <div className={cn('rounded-2xl border p-4 sm:p-5', product.accent.soft, product.accent.border)}>
              <p className={cn('mb-1 text-sm font-semibold', product.accent.text)}>{t('page.whoFor')}</p>
              <p className="text-foreground">{t(`${id}.audience`)}</p>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <ul className="grid h-full grid-cols-1 gap-4">
              {highlights.map((h, i) => (
                <li key={h} className="surface flex items-center gap-4 p-4 sm:p-5">
                  <span className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white bg-gradient-to-br', product.accent.gradient)}>
                    {i + 1}
                  </span>
                  <span className="font-medium">{h}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* --------------------------------------------------------- Features */}
      <section id="features" className="border-y bg-muted/40">
        <div className="container py-16 md:py-24">
          <Reveal>
            <SectionHeader eyebrow={t('page.features')} title={t('page.featuresTitle')} />
          </Reveal>
          <div className="mt-10 grid grid-cols-1 gap-5 sm:mt-12 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((f, i) => {
              const Icon = product.featureIcons[i] ?? product.icon
              return (
                <Reveal key={f.title} delay={(i % 3) * 70}>
                  <div className="surface group h-full p-6 transition-shadow hover:shadow-lift">
                    <span className={cn('mb-5 inline-flex h-11 w-11 items-center justify-center rounded-xl', product.accent.soft, product.accent.text)}>
                      <Icon className="h-5 w-5" />
                    </span>
                    <h3 className="mb-2 text-lg font-semibold">{f.title}</h3>
                    <p className="text-sm leading-relaxed text-muted-foreground">{f.description}</p>
                  </div>
                </Reveal>
              )
            })}
          </div>
        </div>
      </section>

      {/* ----------------------------------------------------- How it works */}
      <section id="how" className="container py-16 md:py-24">
        <Reveal>
          <SectionHeader eyebrow={t('page.howItWorks')} title={t('page.howTitle')} />
        </Reveal>
        <ol className={cn('mt-10 grid grid-cols-1 gap-5 sm:mt-12', steps.length === 4 ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-3')}>
          {steps.map((s, i) => (
            <Reveal as="li" key={s.title} delay={i * 80} className="relative">
              <div className="surface h-full p-6">
                <span className={cn('mb-5 inline-flex h-10 w-10 items-center justify-center rounded-full text-sm font-bold text-white bg-gradient-to-br', product.accent.gradient)}>
                  {i + 1}
                </span>
                <h3 className="mb-2 text-lg font-semibold">{s.title}</h3>
                <p className="text-sm leading-relaxed text-muted-foreground">{s.description}</p>
              </div>
              {i < steps.length - 1 && (
                <ArrowRight
                  aria-hidden
                  className="absolute -end-4 top-1/2 z-10 hidden h-5 w-5 -translate-y-1/2 text-muted-foreground/50 md:block flip-rtl"
                />
              )}
            </Reveal>
          ))}
        </ol>
      </section>

      {/* ------------------------------------------------------ Screenshots */}
      {hasScreens && (
        <section id="screens" className="border-y bg-muted/40">
          <div className="container py-16 md:py-24">
            <Reveal>
              <SectionHeader eyebrow={t('page.screens')} title={t('page.screensTitle')} subtitle={t('page.clickToEnlarge')} />
            </Reveal>
            <div className="mt-10 grid grid-cols-1 gap-6 sm:mt-12 sm:grid-cols-2 lg:grid-cols-3">
              {product.screens.map((s, i) => {
                const caption = t(`${id}.screens.${s.key}`)
                const wide = s.frame === 'plain'
                return (
                  <Reveal key={s.src} delay={(i % 3) * 70} className={cn(wide && 'sm:col-span-2 lg:col-span-3')}>
                    <figure>
                      <button
                        type="button"
                        onClick={() => setLightbox(s.src)}
                        className="group relative block w-full overflow-hidden rounded-2xl border bg-card p-3 text-start shadow-soft transition-shadow hover:shadow-lift focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:p-4"
                      >
                        <div className={cn('flex items-center justify-center', s.frame === 'phone' && 'h-[320px] sm:h-[360px]')}>
                          {s.frame === 'desktop' && (
                            <BrowserFrame className="w-full shadow-none">
                              <img src={s.src} alt={caption} loading="lazy" className="aspect-[16/10] w-full object-cover object-right-top" />
                            </BrowserFrame>
                          )}
                          {s.frame === 'phone' && (
                            <PhoneFrame className="h-full w-auto">
                              <img src={s.src} alt={caption} loading="lazy" className="h-[304px] w-auto object-cover object-top sm:h-[336px]" />
                            </PhoneFrame>
                          )}
                          {s.frame === 'plain' && (
                            <img src={s.src} alt={caption} loading="lazy" className="max-h-[520px] w-full rounded-xl object-contain" />
                          )}
                        </div>
                        <span className="absolute end-3 top-3 rounded-lg bg-background/90 p-1.5 opacity-80 shadow-sm transition-opacity group-hover:opacity-100 sm:opacity-0">
                          <Expand className="h-4 w-4" />
                        </span>
                      </button>
                      <figcaption className="mt-3 px-1 text-sm font-medium">{caption}</figcaption>
                    </figure>
                  </Reveal>
                )
              })}
            </div>
          </div>
        </section>
      )}

      {/* -------------------------------------------------------------- FAQ */}
      <section id="faq" className="container py-16 md:py-24">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_1.6fr] lg:gap-16">
          <Reveal className="space-y-4">
            <SectionHeader align="start" eyebrow={t('page.faq')} title={t('page.faqTitle')} />
            <p className="text-muted-foreground">
              {t('page.faqMore')}{' '}
              <Link to={contactPath({ product: id })} className="font-medium text-primary hover:underline">
                {t('common:actions.contactUs')}
              </Link>
            </p>
          </Reveal>
          <Reveal delay={80}>
            {hasDisclaimer && (
              <div className="mb-6 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                {t(`${id}.disclaimer`)}
              </div>
            )}
            <Accordion type="single" collapsible defaultValue="faq-0" className="surface px-4 sm:px-6">
              {faq.map((item, i) => (
                <AccordionItem key={item.q} value={`faq-${i}`}>
                  <AccordionTrigger>{item.q}</AccordionTrigger>
                  <AccordionContent>{item.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* ---------------------------------------------------- More products */}
      <section className="border-t bg-muted/40">
        <div className="container py-14 sm:py-16">
          <h2 className="mb-6 text-xl font-bold">{t('page.otherProducts')}</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {PRODUCTS.filter((p) => p.id !== id).map((p) => (
              <Link
                key={p.id}
                to={productPath(p.slug)}
                className="surface group flex flex-col gap-3 p-5 transition-all hover:-translate-y-0.5 hover:shadow-lift"
              >
                <div className="flex items-center gap-3">
                  <ProductIconTile product={p} size="sm" />
                  <span className="font-semibold">{t(`${p.id}.name`)}</span>
                </div>
                <p className="line-clamp-2 text-sm text-muted-foreground">{t(`${p.id}.menuLine`)}</p>
                <span className={cn('mt-auto inline-flex items-center gap-1 text-sm font-medium', p.accent.text)}>
                  {t('common:actions.learnMore')}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5 flip-rtl" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CtaBand title={t(`${id}.ctaTitle`)} subtitle={t(`${id}.ctaSubtitle`)} product={id} />
    </>
  )
}
